import { FC, useCallback } from 'react';
import { useMutation } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { Form, Formik } from 'formik';

import AddIcon from '@mui/icons-material/Add';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import Stack from '@mui/material/Stack';
import TextField from 'components/TextField';
import ChipInput from 'components/ChipInput';

import { useErrorHandler } from 'lib';
import { useDialog } from 'components/Dialog';

import { DepartureLocationListAddMutation } from '__generated__/DepartureLocationListAddMutation.graphql';

type FormFields = {
  name: string;
  codes: string[];
};

type Props = {
  connectionID: string;
};

const initial = {
  name: '',
  codes: [],
};

const DepartureLocationListAdd: FC<Props> = ({ connectionID }) => {
  const { close } = useDialog();
  const errorHandler = useErrorHandler();

  const [add, adding] = useMutation<DepartureLocationListAddMutation>(graphql`
    mutation DepartureLocationListAddMutation(
      $input: CreateDepartureLocationInput!
      $connections: [ID!]!
    ) {
      createDepartureLocation(input: $input) {
        node
          @appendNode(
            connections: $connections
            edgeTypeName: "DepartureLocationEdge"
          ) {
          ...DepartureLocationListRow_departureLocation
        }
      }
    }
  `);

  const handleSubmit = useCallback(
    (input: FormFields) => {
      add({
        variables: { connections: [connectionID], input },
        ...errorHandler({
          onCompleted: () => {
            close();
          },
        }),
      });
    },
    [add, close, connectionID, errorHandler]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form>
        <DialogTitle>Nauja išvykimo vieta</DialogTitle>

        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Pridėkite naują išvykimo vietą. Naudojama svetainėje kelionių
            paieškos filtre.
          </DialogContentText>

          <Stack spacing={4}>
            <TextField autoFocus name="name" label="Pavadinimas" fullWidth />
            <ChipInput name="codes" label="Oro uostų kodai" />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={close} variant="text">
            Atšaukti
          </Button>

          <LoadingButton
            loading={adding}
            type="submit"
            variant="contained"
            startIcon={<AddIcon />}>
            Pridėti
          </LoadingButton>
        </DialogActions>
      </Form>
    </Formik>
  );
};

export default DepartureLocationListAdd;
